import { ErrorMessage, Field, Form, Formik } from 'formik';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';
import AuthShell from '../../components/ui/AuthShell';
import { ROUTES } from '../../config/routes';
import { APIServices } from '../../services/axiosCommon';

const ResetSchema = Yup.object({
  password: Yup.string().min(8).max(128).required('Password is required'),
  confirm: Yup.string()
    .oneOf([Yup.ref('password')], "Passwords don't match")
    .required('Confirm your password'),
});

const ResetPasswordView = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const membersid = Number(params.get('membersid') ?? '');
  const rkey = params.get('rkey') ?? '';
  const [serverError, setServerError] = useState<string | null>(null);

  if (!membersid || !rkey) {
    return (
      <AuthShell title="Invalid reset link" subtitle="The link is missing parameters.">
        <Link to={ROUTES.FORGOT_PASSWORD} className="text-sm font-medium text-tfs-primary hover:underline">
          Request a new one →
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Set a new password" subtitle="Choose something at least 8 characters.">
      <Formik
        initialValues={{ password: '', confirm: '' }}
        validationSchema={ResetSchema}
        onSubmit={async ({ password }, { setSubmitting }) => {
          setServerError(null);
          try {
            await APIServices.post('/auth/password-reset/confirm', {
              membersid,
              rkey,
              password,
            });
            navigate(ROUTES.LOGIN, { replace: true });
          } catch (err) {
            const message =
              (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
                ?.message ?? 'Reset failed';
            setServerError(message);
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ isSubmitting }) => (
          <Form className="space-y-4">
            <PasswordField name="password" label="New password" autoComplete="new-password" />
            <PasswordField name="confirm" label="Confirm password" autoComplete="new-password" />

            {serverError && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {serverError}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full rounded-md bg-tfs-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              {isSubmitting ? 'Saving…' : 'Set new password'}
            </button>
          </Form>
        )}
      </Formik>
    </AuthShell>
  );
};

const PasswordField = ({
  name,
  label,
  autoComplete,
}: {
  name: string;
  label: string;
  autoComplete: string;
}) => (
  <div>
    <label htmlFor={name} className="block text-sm font-medium text-slate-700">
      {label}
    </label>
    <Field
      id={name}
      name={name}
      type="password"
      autoComplete={autoComplete}
      className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-tfs-primary focus:outline-none focus:ring-1 focus:ring-tfs-primary"
    />
    <ErrorMessage name={name} component="p" className="mt-1 text-xs text-red-600" />
  </div>
);

export default ResetPasswordView;
